import { AfterViewInit, Component, ElementRef, EventEmitter, OnDestroy, Output } from '@angular/core';

@Component({
  selector: 'app-rebrand-intro-splash',
  templateUrl: './rebrand-intro-splash.component.html',
  styleUrls: ['./rebrand-intro-splash.component.css'],
  standalone: false
})
export class RebrandIntroSplashComponent implements AfterViewInit, OnDestroy {
  @Output() dismiss = new EventEmitter<void>();

  private destroyed = false;

  constructor(private elRef: ElementRef<HTMLElement>) { }

  ngAfterViewInit(): void {
    this.initGlyphDraw();
    this.scheduleTransitionChime();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  onDismiss(): void {
    this.dismiss.emit();
  }

  // El icono de Handly se "dibuja" trazo a trazo: cada path arranca como contorno
  // (stroke-dasharray = su propia longitud) y se rellena al terminar. La longitud
  // solo se puede conocer en runtime (getTotalLength), por eso esto no es CSS puro
  // como el resto del splash.
  private initGlyphDraw(): void {
    try {
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const paths = Array.from(this.elRef.nativeElement.querySelectorAll<SVGPathElement>('.ris-glyph-path'));

      if (reduced) {
        paths.forEach(path => { path.style.fill = 'var(--cf-chrome)'; });
        return;
      }

      paths.forEach((path, i) => {
        const len = path.getTotalLength();
        path.style.strokeDasharray = `${len}`;
        path.style.strokeDashoffset = `${len}`;
        path.style.transition =
          `stroke-dashoffset .85s cubic-bezier(.4,0,.2,1) ${2.3 + i * 0.07}s, ` +
          `fill .35s ease ${2.3 + i * 0.07 + 0.5}s`;
      });

      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (this.destroyed) { return; }
        paths.forEach(path => {
          path.style.strokeDashoffset = '0';
          path.style.fill = 'var(--cf-chrome)';
        });
      }));
    } catch (e) {
      console.warn('No se pudo iniciar la animación del splash de rebranding', e);
    }
  }

  // Pequeño "chime" sincronizado con el flash que cierra el logo viejo (ris-flash,
  // arranca a 1.93s en el CSS) y da paso al icono de Handly. Sintetizado con Web
  // Audio API en vez de un fichero de audio: dos tonos cortos (snap grave -> brillo
  // agudo) con envolvente de volumen, nada que cargar ni licenciar. Respeta
  // prefers-reduced-motion igual que el resto del splash (sin transición visual,
  // el sonido sincronizado no tendría sentido).
  private scheduleTransitionChime(): void {
    try {
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduced) { return; }

      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass) { return; }

      setTimeout(() => {
        if (this.destroyed) { return; }
        this.playTransitionChime(AudioContextClass);
      }, 1900);
    } catch (e) {
      console.warn('No se pudo programar el sonido del splash de rebranding', e);
    }
  }

  private playTransitionChime(AudioContextClass: typeof AudioContext): void {
    try {
      const ctx = new AudioContextClass();
      void ctx.resume().catch(() => {});

      const now = ctx.currentTime;
      const master = ctx.createGain();
      master.gain.setValueAtTime(1, now);
      master.connect(ctx.destination);

      // Nota 1: snap corto y grave, coincide con el cierre de las franjas.
      const snap = ctx.createOscillator();
      snap.type = 'sine';
      snap.frequency.setValueAtTime(520, now);
      snap.frequency.exponentialRampToValueAtTime(880, now + 0.09);

      const snapGain = ctx.createGain();
      snapGain.gain.setValueAtTime(0, now);
      snapGain.gain.linearRampToValueAtTime(0.09, now + 0.015);
      snapGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.28);

      snap.connect(snapGain).connect(master);

      // Nota 2: brillo agudo, ligeramente retrasado, coincide con la aparición del icono.
      const chime = ctx.createOscillator();
      chime.type = 'sine';
      chime.frequency.setValueAtTime(1318.5, now + 0.05);

      const chimeGain = ctx.createGain();
      chimeGain.gain.setValueAtTime(0, now + 0.05);
      chimeGain.gain.linearRampToValueAtTime(0.05, now + 0.07);
      chimeGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.5);

      chime.connect(chimeGain).connect(master);

      snap.start(now);
      snap.stop(now + 0.3);
      chime.start(now + 0.05);
      chime.stop(now + 0.55);

      chime.onended = () => { ctx.close().catch(() => {}); };
    } catch (e) {
      // Decorativo y no crítico (p.ej. autoplay de audio bloqueado por el navegador
      // si no hubo interacción reciente) — nunca debe romper el splash.
      console.warn('No se pudo reproducir el sonido del splash de rebranding', e);
    }
  }
}
